import React, { ReactNode, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  AppBar,
  Box,
  Collapse,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  DirectionsCar as CarIcon,
  People as PeopleIcon,
  Build as ServicesIcon,
  CalendarToday as BookingIcon,
  Assessment as ReportIcon,
  Settings as SettingsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  List as ListIcon,
  BuildCircle as EquipmentIcon,
  Assignment as RequirementsIcon,
  ExpandLess,
  ExpandMore,
  Category as CategoryIcon,
  Description as DescriptionIcon,
  HelpOutline as HelpOutlineIcon,
  AccessTime as AccessTimeIcon,
  VpnKey as VpnKeyIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Inventory as InventoryIcon,
  Assessment as AssessmentIcon,
  Receipt as ReceiptBillIcon,
  CreditCard as CreditCardIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeToggle } from './ThemeToggle';
import menuData from '../data/menuData';

const drawerWidth = 260;

// Icon mapping function
const getIcon = (iconName: string) => {
  const iconMap: { [key: string]: React.ReactNode } = {
    'bx bx-home': <DashboardIcon />,
    'bx bx-calendar-check': <BookingIcon />,
    'bx bx-receipt': <ReceiptIcon />,
    'bx bx-money': <PaymentIcon />,
    'bx bx-user': <PeopleIcon />,
    'bx bx-user-plus': <PeopleIcon />,
    'bx bx-credit-card': <CreditCardIcon />,
    'bx bx-category': <CategoryIcon />,
    'bx bx-list-ul': <ListIcon />,
    'bx bx-plus': <AddIcon />,
    'bx bx-brush': <EquipmentIcon />,
    'bx bx-check-shield': <RequirementsIcon />,
    'bx bx-cog': <SettingsIcon />,
    'bx bx-user-circle': <PeopleIcon />,
    'bx bx-help-circle': <HelpOutlineIcon />,
    'bx bx-time': <AccessTimeIcon />,
    'bx bx-image': <InventoryIcon />,
  };
  return iconMap[iconName] || <CategoryIcon />;
};

const Main = styled('main')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  minHeight: '100vh',
  width: '100%',
  backgroundColor: 'var(--bs-body-bg)',
  [theme.breakpoints.up('md')]: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    padding: theme.spacing(4),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 2),
  minHeight: '64px',
  
  '& .logo-container': {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2, 0),
    '& img': {
      height: 32,
      marginRight: theme.spacing(1.5),
      filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'invert(0)',
    },
    '& h6': {
      color: 'var(--bs-heading-color)',
      fontWeight: 600,
      fontSize: '1.1rem',
      margin: 0,
    },
  },
}));

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path?: string;
  children?: MenuItem[];
  isOpen?: boolean;
  submenu?: Array<{
    text: string;
    available: boolean;
    link: string;
  }>;
}

// Strongly typed shape for raw menu data (from JS file)
interface RawMenuSubItem {
  text: string;
  available: boolean;
  link: string;
}

interface RawMenuItem {
  text: string;
  icon: string;
  available: boolean;
  link: string;
  submenu?: RawMenuSubItem[];
}

interface RawMenuSection {
  header: string;
  items: RawMenuItem[];
}

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItem[]>(() => {
    return (menuData as RawMenuSection[]).map((section) => ({
      text: section.header,
      icon: section.header === 'MAIN MENU' ? <DashboardIcon /> : 
            section.header === 'SERVICES' ? <ServicesIcon /> :
            section.header === 'REPORTS' ? <ReportIcon /> :
            section.header === 'SETTINGS' ? <SettingsIcon /> :
            section.header === 'USER MANAGEMENT' ? <PeopleIcon /> :
            section.header === 'CONTENT MANAGEMENT' ? <DescriptionIcon /> :
            <CategoryIcon />,
      isOpen: false,
      children: section.items.map((item: RawMenuItem) => ({
        text: item.text,
        icon: getIcon(item.icon),
        path: item.link,
        isOpen: false,
        children: item.submenu ? item.submenu.map((sub) => ({
          text: sub.text,
          icon: <ListIcon />,
          path: sub.link,
        })) : undefined
      }))
    }));
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const toggleSubMenu = (index: number) => {
    const updatedMenuItems = [...menuItems];
    updatedMenuItems[index] = {
      ...updatedMenuItems[index],
      isOpen: !updatedMenuItems[index].isOpen
    };
    setMenuItems(updatedMenuItems);
  };

  const renderMenuItems = (items: MenuItem[], depth = 0) => {
    return items.map((item, index) => (
      <React.Fragment key={item.text}>
        <ListItem 
          disablePadding 
          onClick={() => {
            if (item.children) {
              toggleSubMenu(menuItems.findIndex(mi => mi.text === item.text));
            } else if (item.path) {
              handleNavigation(item.path);
            }
          }}
          sx={{
            pl: 2 + (depth * 2),
            bgcolor: location.pathname === item.path ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <ListItemButton>
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
            {item.children && (item.isOpen ? <ExpandLess /> : <ExpandMore />)}
          </ListItemButton>
        </ListItem>
        {item.children && (
          <Collapse in={item.isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {renderMenuItems(item.children, depth + 1)}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    ));
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'var(--bs-body-bg)',
          color: 'var(--bs-body-color)',
          borderBottom: '1px solid var(--bs-border-color)',
          backdropFilter: 'blur(10px)',
          
        }}
      >
        <Toolbar disableGutters sx={{ px: { xs: 2, md: 3 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ 
              mr: 2, 
              display: { md: 'none' },
              color: 'var(--proton-text-primary)'
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box 
             
          />
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              color: 'var(--proton-text-primary)',
              fontWeight: 600,
              fontSize: '1.25rem'
            }}
          >
          </Typography>
          <ThemeToggle />
        </Toolbar>
      </AppBar>
      
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box',
            width: drawerWidth,
            backgroundColor: 'var(--proton-bg-darker)',
            color: 'var(--proton-text-primary)',
            '& .MuiListItemIcon-root': {
              color: 'var(--proton-text-secondary)',
              minWidth: 40
            },
            '& .MuiListItemText-primary': {
              color: 'var(--proton-text-primary)',
              fontSize: '0.9rem',
              fontWeight: 500
            },
            '& .Mui-selected': {
              backgroundColor: 'var(--proton-primary-light) !important',
              '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                color: 'var(--proton-primary)',
              },
            },
            '& .MuiListItemButton-root:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
          },
        }}
      >
        <DrawerHeader>
          <IconButton onClick={handleDrawerToggle}>
            <ChevronLeftIcon />
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {menuItems.map((item, index) => (
            <React.Fragment key={item.text}>
              <ListItem 
                disablePadding 
                onClick={() => {
                  if (item.children) {
                    toggleSubMenu(index);
                  } else if (item.path) {
                    handleNavigation(item.path);
                  }
                }}
                sx={{
                  bgcolor: location.pathname === item.path ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <ListItemButton>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                  {item.children && (item.isOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </ListItem>
              {item.children && (
                <Collapse in={item.isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child) => (
                      <ListItemButton
                        key={child.text}
                        sx={{ pl: 6 }}
                        selected={!!child.path && location.pathname === child.path}
                        onClick={() => child.path && handleNavigation(child.path)}
                      >
                        <ListItemIcon>{child.icon}</ListItemIcon>
                        <ListItemText primary={child.text} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box',
            width: drawerWidth,
            backgroundColor: 'var(--bs-card-bg)',
            color: 'var(--bs-body-color)',
            borderRight: '1px solid var(--bs-border-color)',
            '& .MuiListItemIcon-root': {
              color: 'var(--bs-gray-600)',
              minWidth: 40,
              '& svg': {
                fontSize: '1.25rem',
              },
            },
            '& .MuiListItemText-primary': {
              color: 'var(--bs-body-color)',
              fontSize: '0.9rem',
              fontWeight: 500,
            },
            '& .MuiListItemButton-root': {
              borderRadius: '0.375rem',
              margin: '0.25rem 0.75rem',
              padding: '0.5rem 1rem',
              '&.Mui-selected': {
                backgroundColor: 'rgba(105, 108, 255, 0.15) !important',
                '&:hover': {
                  backgroundColor: 'rgba(105, 108, 255, 0.2) !important',
                },
                '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                  color: 'var(--bs-primary)',
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& .MuiListItemIcon-root': {
                  color: 'var(--bs-primary)',
                },
              },
            },
            '& .MuiCollapse-root .MuiListItemButton-root': {
              paddingLeft: '2.5rem',
              '&.Mui-selected': {
                backgroundColor: 'rgba(105, 108, 255, 0.1) !important',
              },
            },
          },
        }}
        open
      >
        <DrawerHeader>
          <div className="logo-container">
            <img src="/assets/img/logo/proton-logo.png" alt="Proton" />
            
          </div>
        </DrawerHeader>
        <Divider />
        <List>
          {menuItems.map((item, index) => (
            <React.Fragment key={item.text}>
              <ListItem 
                disablePadding 
                onClick={() => {
                  if (item.children) {
                    toggleSubMenu(index);
                  } else if (item.path) {
                    handleNavigation(item.path);
                  }
                }}
                sx={{
                  bgcolor: location.pathname === item.path ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <ListItemButton>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                  {item.children && (item.isOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </ListItem>
              {item.children && (
                <Collapse in={item.isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child) => (
                      <ListItemButton
                        key={child.text}
                        sx={{ pl: 6 }}
                        selected={!!child.path && location.pathname === child.path}
                        onClick={() => child.path && handleNavigation(child.path)}
                      >
                        <ListItemIcon>{child.icon}</ListItemIcon>
                        <ListItemText primary={child.text} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Drawer>
      <Main>
        <DrawerHeader />
        {children}
      </Main>
    </Box>
  );
};

export default Layout;
